<template>
  <dd-modal
    v-model:visible="internalShowModal"
    class="delete-person-modal"
    title="Delete User"
  >
    <loading-placeholder v-if="isLoading" :lines="9" />
    <template v-else>
      <p>
        Once deleted, {{ person.name }} will no longer have access to this account.
        Assign all items currently assigned to {{ person.name }} to:
      </p>
      <p><br></p>
      <single-select
        v-model="userIDToReassignItemsTo"
        class="delete-person-modal__select" 
        placeholder="Select person to re-assign items to..."
      >
        <el-option key="0" label="No one. Delete all assigned items" value="0">
          <avatar :size="24" />
          <span>No one. Delete all assigned items</span>
        </el-option>
        <el-option v-for="person in reassignablePeople" :key="person.id" :label="person.name" :value="person.id">
          <avatar :person="person" :size="24" />
          <span>{{person.name}}</span>
        </el-option>
      </single-select>
      <div v-if="userToReassignItemsTo" class="delete-alert" >
        {{userToReassignItemsTo.name}} will be added as a watcher to any items {{ person.name }} was watching and receive an email informing them of this update.
      </div>
      <dd-checkbox v-model="deleteConfirmed" is-checked dd-size="large">
        I understand this action is not reversible
      </dd-checkbox>
    </template>
    <template v-if="!isLoading" #footer>
      <span>
        <dd-button type="text" @click="clearDeletePersonForm()">Cancel</dd-button>
        <dd-button 
          :is-loading="isDeletingPerson" 
          :is-disabled="isDeleteSubmitDisabled" 
          color="red" 
          @click="deletePerson()"
        >Delete this person</dd-button>
      </span>
    </template>
  </dd-modal>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import {
  IAccountMembershipsOutputDetail,
  IPersonWithEmail,
} from '@/api/types/response.types'

/* API */
import AccountMembershipsAPI from '@/api/modules/account-memberships.api'

export default defineComponent({
  name: 'DeletePersonModal',

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    person: {
      type: Object as () => IAccountMembershipsOutputDetail,
      required: true,
    },
  },

  emits: ['person-deleted', 'update:showModal'],

  data() {
    return {
      isLoading: true,
      isDeletingPerson: false,
      userIDToReassignItemsTo: null as any,
      deleteConfirmed: false,
      reassignablePeople: [] as IPersonWithEmail[],
    }
  },

  computed: {
    internalShowModal: {
      get(): boolean {
        return this.showModal
      },
      set(newValue: boolean) {
        this.$emit('update:showModal', newValue)
      },
    },
    userToReassignItemsTo(): IPersonWithEmail {
      return this.reassignablePeople.find(
        p => p.id === this.userIDToReassignItemsTo,
      )
    },
    isDeleteSubmitDisabled(): boolean {
      return !(this.userIDToReassignItemsTo && this.deleteConfirmed)
    },
  },

  watch: {
    internalShowModal: {
      async handler(value) {
        if (value) {
          this.isLoading = true
          await this.fetchReassignablePeople()
          this.isLoading = false
        }
      },
    },
  },

  methods: {
    clearDeletePersonForm(): void {
      this.userIDToReassignItemsTo = null
      this.deleteConfirmed = false
      this.internalShowModal = false
    },
    async fetchReassignablePeople() {
      try {
        this.reassignablePeople = await AccountMembershipsAPI.get_ReassignmentOptions(
          this.person.accountMembershipID,
        )
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async deletePerson() {
      try {
        this.isDeletingPerson = true
        const response = await AccountMembershipsAPI.post_Delete(
          this.person.accountMembershipID,
          this.userIDToReassignItemsTo,
        )
        this.clearDeletePersonForm()
        this.$messaging.showSuccess(response.message)
        await this.$nextTick()
        this.$emit('person-deleted') // Move route assitant to caller nextr...
      } catch (error) {
        this.$error.emit(error)
      } finally {
        this.isDeletingPerson = false
      }
    },
  },
})
</script>

<style lang="scss">
.delete-person-modal {
  &__select {
    padding-bottom: 20px;
  }

  .delete-alert {
    background-color: $color--orange-15;
    border-radius: 8px;
    padding: 10px;
    margin-bottom: 10px;
    @include lettering--p-sm;
  }
}
</style>
